(() => {
  /* ── Helpers (copied from tisha-product.js) ──────── */
  const parseNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const normalized = value.replace(',', '.');
      const match = normalized.match(/[0-9]+(\.[0-9]+)?/);
      return match ? Number(match[0]) : null;
    }
    return null;
  };

  const parseLength = (value, unitHint) => {
    if (value === null || value === undefined || value === '') return null;
    let unit = (unitHint || '').toString().toLowerCase();
    let raw = value;
    if (typeof value === 'string') {
      raw = value.toLowerCase();
      if (!unit) {
        if (raw.includes('mm')) unit = 'mm';
        else if (raw.includes('cm')) unit = 'cm';
        else if (raw.includes('m')) unit = 'm';
      }
    }
    const num = parseNumber(raw);
    if (!num) return null;
    if (unit === 'mm') return num;
    if (unit === 'cm') return num * 10;
    if (unit === 'm') return num * 1000;
    return num;
  };

  const parseSheetSize = (value, unitHint) => {
    if (!value) return null;
    if (typeof value === 'number') return parseLength(value, unitHint);
    const raw = value.toString().toLowerCase().replace(',', '.');
    const matches = raw.match(/[0-9]+(\.[0-9]+)?/g);
    if (!matches) return null;
    const numbers = matches.map((m) => Number(m)).filter((n) => !Number.isNaN(n));
    if (!numbers.length) return null;
    const longest = Math.max(...numbers);
    return parseLength(longest, unitHint || (raw.includes('cm') ? 'cm' : raw.includes('mm') ? 'mm' : raw.includes('m') ? 'm' : ''));
  };

  const normalizeBulkTiers = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (raw.tiers && Array.isArray(raw.tiers)) return raw.tiers;
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
        if (parsed.tiers) return parsed.tiers;
      } catch (_) { return []; }
    }
    return [];
  };

  const formatMoney = (amount, currency, locale, fractionDigits = 2) => {
    const safeAmount = Number.isFinite(amount) ? amount : 0;
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency', currency,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(safeAmount);
    } catch (_) { return safeAmount.toFixed(fractionDigits); }
  };

  const formatMoneyFromCents = (cents, currency, locale, fractionDigits = 2) =>
    formatMoney((Number(cents) || 0) / 100, currency, locale, fractionDigits);

  /* ── Buyer consent shortening ──────────────────── */
  const initBuyerConsentCopy = () => {
    const applyShortCopy = () => {
      const consent = document.querySelector('#shopify-buyer-consent');
      if (!consent || consent.dataset.shortened === 'true') return;
      const policyButton = consent.querySelector('#shopify-subscription-policy-button');
      if (!policyButton) return;
      const prefix = 'Recurring purchase. By continuing, I agree to the ';
      const suffix = ' and authorize charges at the listed prices and schedule until I cancel.';
      consent.textContent = '';
      consent.append(document.createTextNode(prefix), policyButton, document.createTextNode(suffix));
      consent.dataset.shortened = 'true';
    };
    applyShortCopy();
    new MutationObserver(() => applyShortCopy()).observe(document.body, { childList: true, subtree: true });
  };

  /* ══════════════════════════════════════════════════
     TishaPaperProduct class
     ══════════════════════════════════════════════════ */
  class TishaPaperProduct {
    constructor(root) {
      this.root = root;
      this.data = this.loadData();
      this.locale = document.documentElement.lang || 'en-GB';
      this.currency = root.dataset.currency || 'GBP';
      this.bulkEligible = root.dataset.bulkEligible === 'true';
      this.subscriptionPercent = parseNumber(this.data.subscription_percent || root.dataset.subscriptionPercent) || 0;
      this.showExVat = root.dataset.showExVat === 'true';
      const rawVatRate = parseNumber(root.dataset.vatRate) || 0;
      this.vatRate = rawVatRate <= 1 ? rawVatRate * 100 : rawVatRate;
      this.bulkDiscountPercent = parseNumber(root.dataset.bulkDiscountPercent) || 0;
      this.bulkTiers = normalizeBulkTiers(this.data.bulk_tiers);
      if (!this.bulkTiers.length) {
        this.bulkTiers = [
          { min_qty: 5, percent: 5 },
          { min_qty: 10, percent: 8 },
          { min_qty: 25, percent: 12 },
        ];
      }

      /* DOM refs */
      this.variantCards = Array.from(root.querySelectorAll('[data-variant-card]'));
      this.variantSelects = root.closest('product-info')?.querySelector('variant-selects');
      this.variantIdInput = root.querySelector('.product-variant-id');
      this.quantityInput = root.querySelector('.quantity__input');
      this.qtyStepperButtons = Array.from(root.querySelectorAll('[data-qty-stepper]'));
      this.sheetPriceValue = root.querySelector('[data-sheet-price-value]');
      this.sheetPriceWrap = root.querySelector('[data-sheet-price]');
      this.packPriceValue = root.querySelector('[data-pack-price-value]');
      this.unitPricesWrap = root.querySelector('[data-pack-size]');
      this.priceCurrent = root.querySelector('[data-price-current]');
      this.priceCompare = root.querySelector('[data-price-compare]');
      this.priceExVat = root.querySelector('[data-price-exvat]');
      this.priceSavings = root.querySelector('[data-price-savings]');
      this.purchaseOptions = Array.from(root.querySelectorAll('[data-purchase-option]'));
      this.sellingPlanSelect = root.querySelector('[data-selling-plan-select]');
      this.sellingPlanInput = root.querySelector('[data-selling-plan-input]');
      this.stickyBar = root.querySelector('[data-sticky-bar]');
      this.stickyPrice = root.querySelector('[data-sticky-price]');
      this.stickyButton = root.querySelector('[data-sticky-submit]');
      this.stickyQty = root.querySelector('[data-sticky-qty]');
      this.stickyMode = root.querySelector('[data-sticky-mode]');
      this.stickyModeDd = root.querySelector('[data-sticky-mode-dd]');
      this.stickyModeTrigger = root.querySelector('[data-sticky-mode-trigger]');
      this.stickyModeMenu = root.querySelector('[data-sticky-mode-menu]');
      this.stickyModeOpts = Array.from(root.querySelectorAll('[data-sticky-mode-value]'));
      if (this.stickyModeDd && this.stickyModeTrigger && this.stickyModeMenu) {
        this.stickyModeTrigger.addEventListener('click', (e) => {
          e.stopPropagation();
          this.toggleStickyModeMenu();
        });
        this.stickyModeOpts.forEach((opt) => {
          opt.addEventListener('click', (e) => {
            e.stopPropagation();
            const newMode = opt.dataset.stickyModeValue === 'subscribe' ? 'subscribe' : 'one-time';
            this.purchaseOptions.forEach((o) => {
              const input = o.querySelector('input');
              if (!input) return;
              input.checked = o.dataset.purchaseOption === newMode;
            });
            this.handlePurchaseChange();
            this.closeStickyModeMenu();
          });
        });
        document.addEventListener('click', (e) => {
          if (!this.stickyModeDd.classList.contains('is-open')) return;
          if (!this.stickyModeDd.contains(e.target)) this.closeStickyModeMenu();
        });
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.stickyModeDd.classList.contains('is-open')) {
            this.closeStickyModeMenu();
            this.stickyModeTrigger.focus();
          }
        });
      }

      /* Gallery */
      this.gallery = root.querySelector('[data-tp-gallery]');
      this.mainImage = root.querySelector('[data-gallery-main]');
      this.galleryTrack = root.querySelector('[data-gallery-track]');
      this.gallerySlides = Array.from(root.querySelectorAll('[data-gallery-slide]'));
      this.galleryThumbs = Array.from(root.querySelectorAll('[data-gallery-thumb]'));
      this.galleryDots = Array.from(root.querySelectorAll('[data-gallery-dot]'));
      this.galleryPrevMain = root.querySelector('[data-gallery-prev-main]');
      this.galleryNextMain = root.querySelector('[data-gallery-next-main]');
      this.galleryOpenTriggers = Array.from(root.querySelectorAll('[data-gallery-open]'));
      this.lightbox = root.querySelector('[data-gallery-lightbox]');
      this.lightboxImage = root.querySelector('[data-gallery-lightbox-image]');
      this.lightboxPrev = root.querySelector('[data-gallery-prev]');
      this.lightboxNext = root.querySelector('[data-gallery-next]');
      this.lightboxClose = root.querySelectorAll('[data-gallery-close]');
      this.lightboxCounter = root.querySelector('[data-gallery-counter]');
      this.lbTouchStartX = 0;
      const activeIndex = this.galleryThumbs.findIndex((t) => t.classList.contains('is-active'));
      this.galleryIndex = activeIndex >= 0 ? activeIndex : 0;
      this.galleryScrollFrame = null;
      this.cartRefreshPending = false;

      this.bind();
      this.ensureDefaultVariant();
      this.syncUI();
    }

    loadData() {
      const script = this.root.querySelector('[data-tp-product-data]');
      if (!script) return {};
      try { return JSON.parse(script.textContent || '{}'); }
      catch (_) { return {}; }
    }

    /* ── Event binding ──────────────────────────── */
    bind() {
      this.bindGallery();
      this.bindVariantCards();
      this.bindQuantity();
      this.bindPurchaseOptions();
      this.bindStickyBar();
    }

    bindVariantCards() {
      if (!this.variantCards.length) return;
      this.variantCards.forEach((card) => {
        card.addEventListener('click', () => {
          const id = Number(card.dataset.variantId);
          const variant = this.getVariantById(id);
          if (variant) this.applyVariantSelection(variant);
        });
        const radio = card.querySelector('input[type="radio"]');
        if (radio) {
          radio.addEventListener('change', () => {
            if (!radio.checked) return;
            const id = Number(card.dataset.variantId);
            const variant = this.getVariantById(id);
            if (variant) this.applyVariantSelection(variant);
          });
        }
      });

      if (this.variantSelects) {
        this.variantSelects.addEventListener('change', () => {
          const variant = this.getSelectedVariant();
          if (variant && this.variantIdInput) this.variantIdInput.value = variant.id;
          this.syncUI();
        });
      }
    }

    bindQuantity() {
      if (this.quantityInput) {
        const onQtyChange = () => {
          this.updatePriceDisplay();
          this.updateSheetPrice();
          this.updatePackPrice();
          this.updateStickyQty();
        };
        this.quantityInput.addEventListener('input', onQtyChange);
        this.quantityInput.addEventListener('change', onQtyChange);
      }
      this.qtyStepperButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const action = button.dataset.qtyStepper;
          if (action === 'increase') this.adjustQuantity(1);
          if (action === 'decrease') this.adjustQuantity(-1);
        });
      });
    }

    bindPurchaseOptions() {
      if (!this.purchaseOptions.length) return;
      this.purchaseOptions.forEach((option) => {
        const input = option.querySelector('input');
        if (!input) return;
        option.addEventListener('click', () => {
          this.setPurchaseOption(option.dataset.purchaseOption || 'one-time');
        });
        input.addEventListener('change', () => {
          if (!input.checked) return;
          this.setPurchaseOption(option.dataset.purchaseOption || 'one-time');
        });
      });
      if (this.sellingPlanSelect) {
        this.sellingPlanSelect.addEventListener('change', () => {
          this.updateSellingPlan();
          this.updateSheetPrice();
          this.updatePackPrice();
          this.updatePriceDisplay();
        });
      }
    }

    bindStickyBar() {
      if (!this.stickyBar) return;
      const atcBtn = this.root.querySelector('.tp-atc-btn');
      if (!atcBtn) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          this.stickyBar.classList.toggle('is-visible', !entry.isIntersecting);
        },
        { threshold: 0 }
      );
      observer.observe(atcBtn);
    }

    bindGallery() {
      if (this.galleryThumbs.length && this.mainImage) {
        this.galleryThumbs.forEach((thumb, index) => {
          thumb.addEventListener('click', () => this.setGalleryImage(index));
        });
      }

      /* Dots */
      this.galleryDots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const idx = parseInt(dot.dataset.galleryDot, 10);
          this.setGalleryImage(idx);
        });
      });

      this.galleryOpenTriggers.forEach((trigger) => {
        trigger.addEventListener('click', (e) => {
          /* Don't open lightbox from the img-wrap on mobile — only from slides */
          if (trigger.classList.contains('tp-hero__mobile-slide') || trigger.closest('.tp-hero__mobile-slide')) {
            if (this.gallerySlides.length) {
              const slideIndex = this.gallerySlides.indexOf(trigger);
              this.setGalleryImage(slideIndex >= 0 ? slideIndex : this.galleryIndex);
            }
          }
          this.openLightbox();
        });
      });

      /* Hero img click → open lightbox (both desktop + mobile, hardware-style) */
      const imgWrap = this.root.querySelector('.tp-hero__img-wrap');
      if (imgWrap) {
        let swiped = false;
        let sx = 0, sy = 0, tracking = false;

        imgWrap.addEventListener('touchstart', (e) => {
          if (e.touches.length !== 1) return;
          sx = e.touches[0].clientX;
          sy = e.touches[0].clientY;
          swiped = false;
          tracking = true;
        }, { passive: true });

        imgWrap.addEventListener('touchmove', (e) => {
          if (!tracking) return;
          const dx = e.touches[0].clientX - sx;
          const dy = e.touches[0].clientY - sy;
          if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
            swiped = true;
          }
        }, { passive: true });

        imgWrap.addEventListener('touchend', (e) => {
          if (!tracking) return;
          tracking = false;
          if (!swiped) return;
          const dx = e.changedTouches[0].clientX - sx;
          if (Math.abs(dx) > 50) {
            this.stepGallery(dx < 0 ? 1 : -1);
          }
        }, { passive: true });

        imgWrap.addEventListener('click', (e) => {
          if (swiped) {
            e.preventDefault();
            e.stopPropagation();
            swiped = false;
            return;
          }
          /* Skip if click was on a thumb (handled separately) */
          if (e.target.closest('[data-gallery-thumb]')) return;
          /* Skip if click was on a slide button in mobile track */
          if (e.target.closest('.tp-hero__mobile-slide')) return;
          this.openLightbox();
        });
      }

      if (this.galleryPrevMain) this.galleryPrevMain.addEventListener('click', () => this.stepGallery(-1));
      if (this.galleryNextMain) this.galleryNextMain.addEventListener('click', () => this.stepGallery(1));
      if (this.lightboxPrev) this.lightboxPrev.addEventListener('click', () => this.stepGallery(-1));
      if (this.lightboxNext) this.lightboxNext.addEventListener('click', () => this.stepGallery(1));
      if (this.lightboxClose.length) {
        this.lightboxClose.forEach((btn) => btn.addEventListener('click', () => this.closeLightbox()));
      }

      document.addEventListener('keydown', (event) => {
        if (!this.lightbox || this.lightbox.hasAttribute('hidden')) return;
        if (event.key === 'Escape') this.closeLightbox();
        if (event.key === 'ArrowLeft') this.stepGallery(-1);
        if (event.key === 'ArrowRight') this.stepGallery(1);
      });

      /* Mobile swipe track — sync dots on scroll */
      if (this.galleryTrack && this.gallerySlides.length) {
        this.galleryTrack.addEventListener('scroll', () => {
          if (this.galleryScrollFrame) return;
          this.galleryScrollFrame = window.requestAnimationFrame(() => {
            this.galleryScrollFrame = null;
            const slideWidth = this.galleryTrack.clientWidth || 1;
            const index = Math.round(this.galleryTrack.scrollLeft / slideWidth);
            this.setGalleryImage(index, { skipScroll: true });
          });
        });
      }

      /* Lightbox touch swipe */
      if (this.lightboxImage) {
        this.lightboxImage.addEventListener('touchstart', (e) => {
          this.lbTouchStartX = e.touches[0].clientX;
        }, { passive: true });

        this.lightboxImage.addEventListener('touchend', (e) => {
          const diff = this.lbTouchStartX - e.changedTouches[0].clientX;
          if (Math.abs(diff) > 50) {
            if (diff > 0) this.stepGallery(1);
            else this.stepGallery(-1);
          }
        }, { passive: true });
      }
    }

    /* ── Variant logic ──────────────────────────── */
    ensureDefaultVariant() {
      if (!this.variantCards.length || !this.data.variants?.length) return;
      if (window?.location?.search && new URLSearchParams(window.location.search).has('variant')) return;
      const current = this.getSelectedVariant();
      if (current?.available) return;
      const target = this.data.variants.find((v) => v.available) || this.data.variants[0];
      if (target && (!current || Number(target.id) !== Number(current.id))) {
        this.applyVariantSelection(target);
      }
    }

    getSelectedVariant() {
      if (this.variantSelects) {
        const selects = Array.from(this.variantSelects.querySelectorAll('select'));
        if (selects.length) {
          const selectedValues = selects.map((s) => s.value);
          const match = (this.data.variants || []).find((v) =>
            v.options && v.options.every((opt, i) => opt === selectedValues[i])
          );
          if (match) return match;
        }
      }
      const id = this.variantIdInput ? Number(this.variantIdInput.value) : null;
      return this.getVariantById(id) || (this.data.variants || [])[0];
    }

    getVariantById(id) {
      if (!id) return null;
      return (this.data.variants || []).find((v) => Number(v.id) === Number(id)) || null;
    }

    applyVariantSelection(variant) {
      if (this.variantSelects && variant.options) {
        const selects = Array.from(this.variantSelects.querySelectorAll('select'));
        selects.forEach((select, index) => {
          const nextValue = variant.options[index];
          if (!nextValue || select.value === nextValue) return;
          select.value = nextValue;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
      if (this.variantIdInput) this.variantIdInput.value = variant.id;
      this.syncUI();
    }

    /* ── Purchase mode ──────────────────────────── */
    getPurchaseMode() {
      const selected = this.purchaseOptions.find((o) => o.querySelector('input')?.checked);
      return selected?.dataset.purchaseOption || 'one-time';
    }

    setPurchaseOption(mode) {
      this.purchaseOptions.forEach((option) => {
        const input = option.querySelector('input');
        const isMatch = option.dataset.purchaseOption === mode;
        if (input) input.checked = isMatch;
        option.classList.toggle('active', isMatch);
      });
      this.updateSellingPlan();
      this.updatePriceDisplay();
      this.updateSheetPrice();
      this.updatePackPrice();
      this.updateStickyMode();
    }

    updatePackPrice() {
      if (!this.packPriceValue || !this.unitPricesWrap) return;
      const packSize = parseNumber(this.unitPricesWrap.dataset.packSize);
      if (!packSize) return;
      const variant = this.getSelectedVariant();
      if (!variant) return;
      const discountPercent = this.getDiscountPercent();
      const basePrice = Number(variant.price || 0);
      const effectivePrice = basePrice * (1 - discountPercent / 100);
      const perPackCents = effectivePrice / packSize;
      this.packPriceValue.textContent = formatMoneyFromCents(perPackCents, this.currency, this.locale, 2);
    }

    updateSellingPlan() {
      if (!this.sellingPlanInput) return;
      const mode = this.getPurchaseMode();
      if (mode === 'subscribe' && this.sellingPlanSelect) {
        this.sellingPlanInput.value = this.sellingPlanSelect.value || '';
      } else {
        this.sellingPlanInput.value = '';
      }
      this.root.dataset.purchaseMode = mode;
    }

    /* ── Pricing calculations ───────────────────── */
    getSheetsPerPack(variant) {
      if (!variant) return null;
      const directSheets = parseNumber(variant.sheets_per_pack) || parseNumber(this.root.dataset.sheetsPerPack);
      if (directSheets) return directSheets;

      let sheetsPerRoll = parseNumber(this.root.dataset.sheetsPerRoll);
      if (!sheetsPerRoll) {
        const rollLength = parseLength(this.root.dataset.rollLength, this.root.dataset.rollLengthUnit);
        let sheetLength = parseLength(this.root.dataset.sheetLength, this.root.dataset.sheetLengthUnit);
        if (!sheetLength) sheetLength = parseSheetSize(this.root.dataset.sheetSize, this.root.dataset.sheetLengthUnit);
        if (rollLength && sheetLength) sheetsPerRoll = Math.floor(rollLength / sheetLength);
      }
      return sheetsPerRoll || null;
    }

    getBulkDiscountForQty(qty) {
      if (!this.bulkEligible) return 0;
      let percent = 0;
      this.bulkTiers.forEach((tier) => {
        if (tier && tier.min_qty && tier.percent && qty >= tier.min_qty) {
          percent = Math.max(percent, Number(tier.percent) || 0);
        }
      });
      return percent;
    }

    getDiscountPercent() {
      const mode = this.getPurchaseMode();
      if (mode === 'subscribe') return this.subscriptionPercent;
      const qty = parseNumber(this.quantityInput?.value) || 0;
      return this.getBulkDiscountForQty(qty);
    }

    updateSheetPrice() {
      if (!this.sheetPriceValue || !this.sheetPriceWrap) return;
      const variant = this.getSelectedVariant();
      if (!variant) { this.sheetPriceValue.textContent = '--'; return; }

      const priceMode = this.sheetPriceWrap.dataset.priceMode || 'sheets';
      const discountPercent = this.getDiscountPercent();
      const basePrice = Number(variant.price || 0);
      const effectivePrice = basePrice * (1 - discountPercent / 100);

      if (priceMode === 'metre') {
        // parseLength returns length in MILLIMETRES (internal unit). Convert to metres.
        const rollLengthMm = parseLength(this.root.dataset.rollLength, this.root.dataset.rollLengthUnit);
        const packsPerCase = parseNumber(this.root.dataset.packsPerCase) || parseNumber(this.data.packs_per_case) || 1;
        const totalMetres = rollLengthMm ? (rollLengthMm * packsPerCase) / 1000 : 0;
        if (!totalMetres) { this.sheetPriceValue.textContent = '--'; return; }
        // £ per 100m — more readable than per single metre (avoids £0.009 fractions).
        const per100mCurrency = ((effectivePrice / 100) / totalMetres) * 100;
        this.sheetPriceValue.textContent = formatMoney(per100mCurrency, this.currency, this.locale, 2);
        // Update label dynamically
        const labelEl = this.sheetPriceWrap.querySelector('[data-sheet-price-label]');
        if (labelEl) labelEl.textContent = ' / 100 metres';
      } else {
        // Convention: variant.metafields.custom.sheets_per_pack stores TOTAL sheets per case
        // (not per single pack). The numeric identity (cents/total = £/100sheets) makes the
        // formula work without explicit ×100 or ÷100 conversion.
        const totalSheetsPerCase = this.getSheetsPerPack(variant);
        if (!totalSheetsPerCase) { this.sheetPriceValue.textContent = '--'; return; }
        const per100Currency = effectivePrice / totalSheetsPerCase;
        this.sheetPriceValue.textContent = formatMoney(per100Currency, this.currency, this.locale, 2);
      }
    }

    updatePriceDisplay() {
      if (!this.priceCurrent) return;
      const variant = this.getSelectedVariant();
      if (!variant) return;
      const quantity = parseNumber(this.quantityInput?.value) || 1;
      const basePrice = Number(variant.price || 0);
      const compareAt = Number(variant.compare_at_price || 0);
      const baseTotal = basePrice * quantity;
      const discountPercent = this.getDiscountPercent();
      let displayTotal = baseTotal;
      let comparePrice = 0;

      if (discountPercent > 0) {
        displayTotal = Math.round(baseTotal * (1 - discountPercent / 100));
        comparePrice = baseTotal;
      } else if (compareAt > basePrice) {
        comparePrice = compareAt * quantity;
      }

      this.priceCurrent.textContent = formatMoneyFromCents(displayTotal, this.currency, this.locale, 2);

      if (this.priceCompare) {
        if (comparePrice > 0) {
          this.priceCompare.textContent = formatMoneyFromCents(comparePrice, this.currency, this.locale, 2);
          this.priceCompare.removeAttribute('hidden');
        } else {
          this.priceCompare.setAttribute('hidden', '');
        }
      }

      if (this.priceExVat && this.showExVat) {
        const divisor = 1 + this.vatRate / 100;
        const exVatCents = Math.round(displayTotal / divisor);
        this.priceExVat.textContent = `${formatMoneyFromCents(exVatCents, this.currency, this.locale, 2)} ex VAT`;
      }

      if (this.priceSavings) {
        const savings = Math.max(0, comparePrice - displayTotal);
        if (savings > 0) {
          this.priceSavings.textContent = `Save ${formatMoneyFromCents(savings, this.currency, this.locale, 2)}`;
          this.priceSavings.removeAttribute('hidden');
        } else {
          this.priceSavings.setAttribute('hidden', '');
        }
      }

      if (this.stickyPrice) {
        this.stickyPrice.textContent = formatMoneyFromCents(displayTotal, this.currency, this.locale, 2);
      }

      this.updatePackCardBadges();
    }

    updatePackCardBadges() {
      if (!this.variantCards.length) return;
      const mode = this.getPurchaseMode();
      const qty = parseNumber(this.quantityInput?.value) || 0;
      const percent = mode === 'subscribe' ? 0 : this.getBulkDiscountForQty(qty);
      this.variantCards.forEach((card) => {
        const badge = card.querySelector('[data-pack-badge]');
        if (!badge) return;
        if (percent > 0 && mode === 'one-time') {
          badge.textContent = `-${percent}%`;
          badge.removeAttribute('hidden');
        } else {
          badge.setAttribute('hidden', '');
        }
      });
    }

    /* ── Quantity ────────────────────────────────── */
    adjustQuantity(delta) {
      if (!this.quantityInput) return;
      const step = parseNumber(this.quantityInput.step) || 1;
      const min = parseNumber(this.quantityInput.min) || 1;
      const max = parseNumber(this.quantityInput.max);
      let current = parseNumber(this.quantityInput.value);
      if (!current || current < min) current = min;
      let next = current + delta * step;
      if (next < min) next = min;
      if (Number.isFinite(max) && next > max) next = max;
      this.quantityInput.value = next;
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    updateStickyQty() {
      if (!this.stickyQty) return;
      const qty = parseNumber(this.quantityInput?.value) || 1;
      this.stickyQty.textContent = `Qty: ${qty}`;
    }

    updateStickyMode() {
      const mode = this.getPurchaseMode();
      if (this.stickyModeDd) {
        this.stickyModeDd.classList.toggle('is-subscribe', mode === 'subscribe');
        this.stickyModeDd.classList.toggle('is-onetime', mode !== 'subscribe');
      }
      if (this.stickyModeOpts && this.stickyModeOpts.length) {
        this.stickyModeOpts.forEach((opt) => {
          const isMatch = opt.dataset.stickyModeValue === mode;
          opt.classList.toggle('is-selected', isMatch);
          opt.setAttribute('aria-selected', isMatch ? 'true' : 'false');
        });
      }
      if (!this.stickyMode) return;
      if (mode === 'subscribe') {
        this.stickyMode.textContent = 'Auto-Restock';
      } else {
        this.stickyMode.textContent = 'One-time';
      }
    }

    toggleStickyModeMenu() {
      if (!this.stickyModeDd) return;
      if (this.stickyModeDd.classList.contains('is-open')) {
        this.closeStickyModeMenu();
      } else {
        this.openStickyModeMenu();
      }
    }
    openStickyModeMenu() {
      if (!this.stickyModeDd || !this.stickyModeMenu || !this.stickyModeTrigger) return;
      this.stickyModeMenu.hidden = false;
      // force reflow so the transition runs after `hidden` removal
      void this.stickyModeMenu.offsetWidth;
      this.stickyModeDd.classList.add('is-open');
      this.stickyModeTrigger.setAttribute('aria-expanded', 'true');
    }
    closeStickyModeMenu() {
      if (!this.stickyModeDd || !this.stickyModeMenu || !this.stickyModeTrigger) return;
      this.stickyModeDd.classList.remove('is-open');
      this.stickyModeTrigger.setAttribute('aria-expanded', 'false');
      // hide after transition so it's removed from a11y tree
      setTimeout(() => {
        if (!this.stickyModeDd.classList.contains('is-open')) this.stickyModeMenu.hidden = true;
      }, 180);
    }

    /* ── Variant card UI ────────────────────────── */
    setActiveCard(variantId) {
      this.variantCards.forEach((card) => {
        const isActive = Number(card.dataset.variantId) === Number(variantId);
        card.classList.toggle('active', isActive);
        const radio = card.querySelector('input[type="radio"]');
        if (radio) radio.checked = isActive;
      });
    }

    /* ── Full UI sync ───────────────────────────── */
    syncUI() {
      const variant = this.getSelectedVariant();
      if (variant) this.setActiveCard(variant.id);
      // SKU — показуємо код обраного варіанта (правки Ольги 20.10.2026)
      const skuEl = this.root.querySelector('[data-pdp-sku]');
      if (skuEl && variant?.sku) skuEl.textContent = variant.sku;
      // аркушів у пачці — для обраного варіанта (серветки 80 / 100)
      const packSheetsEl = this.root.querySelector('[data-tp-pack-sheets]');
      if (packSheetsEl && variant?.pack_sheets) packSheetsEl.textContent = variant.pack_sheets;
      // фото варіанта (накладки TC150 / TC200): лише коли варіант змінився, щоб не збивати ручне гортання
      const mediaId = Number(variant?.featured_media_id) || 0;
      if (mediaId && mediaId !== this.lastVariantMediaId) {
        this.lastVariantMediaId = mediaId;
        const nodes = this.galleryThumbs.length ? this.galleryThumbs : this.gallerySlides;
        const idx = nodes.findIndex((n) => Number(n.dataset.mediaId) === mediaId);
        if (idx >= 0 && idx !== this.galleryIndex) this.setGalleryImage(idx);
      }
      this.handlePurchaseChange();
      this.updateStickyQty();
      this.updateStickyMode();

      const mainSubmit = this.root.querySelector('.product-form__submit');
      const isAvailable = Boolean(variant?.available);

      if (this.variantIdInput) this.variantIdInput.disabled = !isAvailable;

      if (mainSubmit) {
        mainSubmit.disabled = !isAvailable;
        const label = mainSubmit.querySelector('span');
        if (label) {
          label.textContent = isAvailable
            ? (window.variantStrings?.addToCart || 'Add to cart')
            : (window.variantStrings?.soldOut || 'Sold out');
        }
      }

      if (this.stickyButton) this.stickyButton.disabled = !isAvailable;
    }

    handlePurchaseChange() {
      const selected = this.purchaseOptions.find((o) => o.querySelector('input')?.checked);
      const mode = selected?.dataset.purchaseOption || 'one-time';
      this.setPurchaseOption(mode);
      this.updateStickyMode();
    }

    /* ── Gallery ─────────────────────────────────── */
    setGalleryImage(index, { skipScroll = false } = {}) {
      const total = this.galleryThumbs.length || this.gallerySlides.length;
      if (!total) return;
      const safeIndex = ((index % total) + total) % total;

      /* Update desktop main image + thumbs */
      if (this.mainImage && this.galleryThumbs.length) {
        const thumb = this.galleryThumbs[safeIndex];
        const src = thumb?.dataset.full;
        if (src) {
          this.mainImage.src = src;
          this.mainImage.alt = thumb.dataset.alt || '';
        }
        this.galleryThumbs.forEach((t) => t.classList.remove('is-active'));
        if (thumb) thumb.classList.add('is-active');
      }

      /* Update dots */
      this.galleryDots.forEach((d) => d.classList.remove('is-active'));
      if (this.galleryDots[safeIndex]) this.galleryDots[safeIndex].classList.add('is-active');

      this.galleryIndex = safeIndex;

      /* Scroll mobile track */
      if (this.galleryTrack && this.gallerySlides.length && !skipScroll) {
        const slideWidth = this.galleryTrack.clientWidth || 1;
        this.galleryTrack.scrollTo({ left: slideWidth * safeIndex, behavior: 'smooth' });
      }
    }

    updateLightboxCounter() {
      if (!this.lightboxCounter) return;
      const total = this.galleryThumbs.length || this.gallerySlides.length;
      this.lightboxCounter.textContent = `${this.galleryIndex + 1} / ${total}`;
    }

    openLightbox() {
      if (!this.lightbox || !this.lightboxImage) return;
      this.lightbox.removeAttribute('hidden');
      document.documentElement.classList.add('tp-lightbox-open');
      document.body.classList.add('tp-lightbox-open');
      const current = this.galleryThumbs[this.galleryIndex];
      const src = current?.dataset.full || this.mainImage?.src;
      this.lightboxImage.src = src || '';
      this.lightboxImage.alt = current?.dataset.alt || '';
      this.updateLightboxCounter();
    }

    closeLightbox() {
      if (!this.lightbox) return;
      this.lightbox.setAttribute('hidden', '');
      document.documentElement.classList.remove('tp-lightbox-open');
      document.body.classList.remove('tp-lightbox-open');
    }

    stepGallery(step) {
      const total = this.galleryThumbs.length || this.gallerySlides.length;
      if (!total) return;
      this.setGalleryImage(this.galleryIndex + step);
      if (!this.lightbox || this.lightbox.hasAttribute('hidden') || !this.lightboxImage) return;
      const current = this.galleryThumbs[this.galleryIndex];
      this.lightboxImage.src = current?.dataset.full || '';
      this.lightboxImage.alt = current?.dataset.alt || '';
      this.updateLightboxCounter();
    }

    /* ── Cart update handling ───────────────────── */
    handleCartUpdate() {
      this.updateStickyQty();
      this.refreshCartUi();
    }

    refreshCartUi() {
      if (this.cartRefreshPending) return;
      this.cartRefreshPending = true;

      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) {
        this.refreshCartBubbleOnly().finally(() => { this.cartRefreshPending = false; });
        return;
      }

      const cartUrl = window.routes?.cart_url || '/cart';
      fetch(`${cartUrl}?section_id=cart-drawer`)
        .then((r) => r.text())
        .then((html) => {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const sourceDrawer = parsed.querySelector('#CartDrawer');
          const targetDrawer = cartDrawer.querySelector('#CartDrawer');
          if (sourceDrawer && targetDrawer) targetDrawer.innerHTML = sourceDrawer.innerHTML;
          const sourceRoot = parsed.querySelector('cart-drawer');
          if (sourceRoot) cartDrawer.classList.toggle('is-empty', sourceRoot.classList.contains('is-empty'));
          if (typeof cartDrawer.open === 'function') cartDrawer.open();
        })
        .catch(console.error)
        .finally(() => {
          this.refreshCartBubbleOnly().finally(() => { this.cartRefreshPending = false; });
        });
    }

    refreshCartBubbleOnly() {
      const bubble = document.getElementById('cart-icon-bubble');
      if (!bubble) return Promise.resolve();
      const cartUrl = window.routes?.cart_url || '/cart';
      return fetch(`${cartUrl}?section_id=cart-icon-bubble`)
        .then((r) => r.text())
        .then((html) => {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const source = parsed.getElementById('cart-icon-bubble') || parsed.querySelector('.shopify-section');
          if (source) bubble.innerHTML = source.innerHTML;
        })
        .catch(console.error);
    }
  }

  /* ══════════════════════════════════════════════════
     Init + AJAX ATC + PubSub
     ══════════════════════════════════════════════════ */
  const tpInstances = new WeakMap();
  let cartUpdateSubscribed = false;

  const subscribeToCartUpdates = () => {
    if (cartUpdateSubscribed) return;
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    cartUpdateSubscribed = true;
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      document.querySelectorAll('[data-tp-product]').forEach((root) => {
        const instance = tpInstances.get(root);
        if (instance) instance.handleCartUpdate(event);
      });
    });
    subscribe(PUB_SUB_EVENTS.quantityUpdate, () => {
      document.querySelectorAll('[data-tp-product]').forEach((root) => {
        const instance = tpInstances.get(root);
        if (instance) instance.updateStickyQty();
      });
    });
  };

  const bindAjaxAddToCart = (scope = document) => {
    scope.querySelectorAll('[data-tp-product] form[data-type="add-to-cart-form"]').forEach((form) => {
      if (form.dataset.tpAjaxAtc === '1') return;
      form.dataset.tpAjaxAtc = '1';

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (form.dataset.tpSubmitting === '1') return;

        const submitButton = form.querySelector('[type="submit"]');
        const spinner = submitButton?.querySelector('.loading__spinner') || form.querySelector('.loading__spinner');
        const errorWrapper = form.querySelector('.product-form__error-message-wrapper');
        const errorEl = form.querySelector('.product-form__error-message');

        const setError = (msg = '') => {
          if (!errorWrapper || !errorEl) return;
          if (msg) { errorEl.textContent = msg; errorWrapper.hidden = false; }
          else { errorEl.textContent = ''; errorWrapper.hidden = true; }
        };

        const setLoading = (state) => {
          if (!submitButton) return;
          if (state) {
            submitButton.setAttribute('aria-disabled', 'true');
            submitButton.classList.add('loading');
            spinner?.classList.remove('hidden');
          } else {
            submitButton.removeAttribute('aria-disabled');
            submitButton.classList.remove('loading');
            spinner?.classList.add('hidden');
          }
        };

        const getFetchConfig = () => {
          if (typeof fetchConfig === 'function') return fetchConfig('javascript');
          return {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          };
        };

        setError('');
        setLoading(true);
        form.dataset.tpSubmitting = '1';

        try {
          const baseUrl = window.routes?.cart_add_url || '/cart/add';
          const addUrl = baseUrl.endsWith('.js') ? baseUrl : `${baseUrl}.js`;
          const config = getFetchConfig();
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
          config.body = new FormData(form);

          const response = await fetch(addUrl, config);
          let data = {};
          try { data = await response.json(); } catch (_) { data = {}; }

          if (data.status) {
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'tisha-product-paper',
                productVariantId: form.querySelector('[name="id"]')?.value,
                errors: data.errors || data.description,
                message: data.message,
              });
            }
            setError(data.description || data.message || 'Could not add to cart');
            return;
          }

          document.dispatchEvent(new CustomEvent('tisha:cart:added'));
        } catch (err) {
          console.error(err);
          setError('Could not add to cart. Please try again.');
        } finally {
          form.dataset.tpSubmitting = '0';
          setLoading(false);
        }
      }, true);
    });
  };

  const initTishaPaperProducts = (scope = document) => {
    scope.querySelectorAll('[data-tp-product]').forEach((root) => {
      if (root.dataset.tpInitialized === 'true') return;
      root.dataset.tpInitialized = 'true';
      const instance = new TishaPaperProduct(root);
      tpInstances.set(root, instance);
    });
    bindAjaxAddToCart(scope);
    subscribeToCartUpdates();
  };

  document.addEventListener('DOMContentLoaded', () => {
    initBuyerConsentCopy();
    initTishaPaperProducts(document);
  });

  document.addEventListener('product-info:loaded', (event) => {
    initTishaPaperProducts(event.target);
  });
})();
